import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import { type MaybeRefOrGetter, type Ref, toValue } from "vue";

import { pb, pbCollectionNames } from "#api/pocketbase";
import type {
  CollaboratorSeasonFeedbackStats,
  CollaboratorSeasonReviewsStats,
  CollaboratorStats,
  ComposerStats,
  SeasonStats,
  SeasonSubmissionsStats,
} from "./stats.types";

// NOTE: Query keys used for invalidation should require all arguments, as 'undefined' in query key
//         prevents invalidating any queries with similar key but that do provide args for that argument!
export const statKeys = {
  platformAll: ["stats", "platform"],
  platformCollaborators: () => [...statKeys.platformAll, "collaborators"],
  platformComposers: () => [...statKeys.platformAll, "composers"],
  platformFeedback: () => [...statKeys.platformAll, "feedback"],
  platformSeasons: () => [...statKeys.platformAll, "seasons"],
  platformSeasonFeedback: (seasonId: Ref<string | undefined>) => [
    ...statKeys.platformFeedback(),
    "season",
    seasonId,
  ],
  platformReviews: () => [...statKeys.platformAll, "reviews"],
  platformSeasonReviews: (seasonId: Ref<string | undefined>) => [
    ...statKeys.platformReviews(),
    "season",
    seasonId,
  ],
  platformSubmissions: () => [...statKeys.platformAll, "submissions"],
  platformSeasonSubmissions: (seasonId: Ref<string | undefined>) => [
    ...statKeys.platformSubmissions(),
    "season",
    seasonId,
  ],
} as const;

export const usePlatformStatsCollaboratorsQuery = () => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformCollaborators(),
    queryFn: async () => {
      return pb.send<CollaboratorStats>("/api/stats/platform/collaborator", {});
    },
  });
};

export const usePlatformStatsComposersQuery = () => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformComposers(),
    queryFn: async () => {
      return pb.send<ComposerStats>("/api/stats/platform/composer", {});
    },
  });
};

export const usePlatformStatsSeasonsQuery = () => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformSeasons(),
    queryFn: async () => {
      return pb.send<SeasonStats[]>("/api/stats/platform/season", {});
    },
  });
};

export const usePlatformStatsSeasonFeedbackQuery = (
  seasonId: Ref<string | undefined>,
  args?: { enabled?: Ref<boolean> },
) => {
  return useQuery({
    enabled: () => args?.enabled && !!seasonId.value,
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformSeasonFeedback(seasonId),
    queryFn: async () => {
      return pb.send<CollaboratorSeasonFeedbackStats[]>(
        `/api/stats/platform/season/${seasonId.value}/feedback`,
        {},
      );
    },
  });
};

export const usePlatformStatsSeasonReviewsQuery = (
  seasonId: Ref<string | undefined>,
  args?: { enabled?: Ref<boolean> },
) => {
  return useQuery({
    enabled: () => args?.enabled && !!seasonId.value,
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformSeasonReviews(seasonId),
    queryFn: async () => {
      return pb.send<CollaboratorSeasonReviewsStats[]>(
        `/api/stats/platform/season/${seasonId.value}/review`,
        {},
      );
    },
  });
};

/** All submission stats */
export const usePlatformStatsAllSubmissionsQuery = (enabled?: MaybeRefOrGetter<boolean>) => {
  return useQuery({
    enabled,
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformSubmissions(),
    queryFn: async () => {
      const submissions = await pb
        .collection(pbCollectionNames.submissions)
        .getList<unknown>(1, 1, {
          fields: "id",
        });
      return {
        total: submissions.totalItems,
      };
    },
  });
};

export const usePlatformStatsSeasonSubmissionsQuery = (
  seasonId: Ref<string | undefined>,
  enabled?: MaybeRefOrGetter<boolean>,
) => {
  return useQuery({
    enabled: () => !!seasonId.value && (enabled ? toValue(enabled) : true),
    placeholderData: keepPreviousData,
    queryKey: statKeys.platformSeasonSubmissions(seasonId),
    queryFn: async () => {
      return pb.send<SeasonSubmissionsStats>(
        `/api/stats/platform/season/${seasonId.value}/submission`,
        {},
      );
    },
  });
};
